import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  InputGroup,
  Alert,
  Card,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  IconPlus,
  IconMinus,
  IconPackage,
  IconCheck,
  IconX,
  IconShoppingCart,
  IconSearch,
  IconSelectAll,
  IconDeselect,
} from "@tabler/icons-react";
import { postRequest, patchRequest, getRequest } from "services/api";
import { useCategory } from "../context/CategoryContext";
import Swal from "sweetalert2";

const StockUpdateModal = ({
  show,
  onHide,
  selectedItem,
  onSuccess,
  onError,
  warehouses = [],
}) => {
  const { categories } = useCategory();
  const [activeTab, setActiveTab] = useState("single");
  const [singleUpdate, setSingleUpdate] = useState({
    quantity: 0,
    type: "add", // "add" or "subtract"
    reason: "",
    warehouse_id: "", // Use warehouse_id instead of warehouse string
  });

  const [bulkUpdates, setBulkUpdates] = useState([]);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkType, setBulkType] = useState("in"); // Add bulk type state
  const [bulkSearchTerm, setBulkSearchTerm] = useState("");
  const [bulkCategoryFilter, setBulkCategoryFilter] = useState("");
  const [bulkStockFilter, setBulkStockFilter] = useState("all");
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [allCategoriesData, setAllCategoriesData] = useState([]);
  const [bulkCurrentPage, setBulkCurrentPage] = useState(1);
  const [bulkPageSize, setBulkPageSize] = useState(20);

  const [loading, setLoading] = useState(false);

  // Fetch all inventory data for bulk updates
  const fetchAllInventory = async () => {
    try {
      const response = await getRequest(
        "/inventory?limit=10000&include=category"
      );
      if (response.success) {
        setAllInventoryData(response.data || []);
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch all inventory:", error);
      return [];
    }
  };

  // Fetch all categories for bulk updates
  const fetchAllCategories = async () => {
    try {
      const response = await getRequest("/inventory-category?limit=10000");
      if (response.success) {
        setAllCategoriesData(response.data || []);
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch all categories:", error);
      return [];
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setSingleUpdate({
        quantity: 0,
        type: "add",
        reason: "",
        warehouse_id: warehouses.length > 0 ? warehouses[0].id : "", // Set default to first warehouse
      });
      setBulkReason("");
      setBulkType("in"); // Reset bulk type
      setBulkSearchTerm("");
      setBulkCategoryFilter("");
      setBulkStockFilter("all");
      setBulkCurrentPage(1);

      // For bulk updates, fetch all inventory data and categories
      if (!selectedItem) {
        fetchAllInventory().then((allData) => {
          setBulkUpdates(
            allData.map((item) => ({
              id: item.id,
              name: item.name,
              currentStock: item.current_stock,
              unit: item.unit,
              quantity: 0,
              selected: false,
            }))
          );
        });
        fetchAllCategories();
      } else {
        // For single updates, use the provided items
        setBulkUpdates([]);
        setAllInventoryData([]);
        setAllCategoriesData([]);
      }

      setActiveTab(selectedItem ? "single" : "bulk");
    }
  }, [show, selectedItem]);

  // Filter bulk items (show all filtered items, no pagination)
  const filteredBulkItems = useMemo(() => {
    let filtered = bulkUpdates;

    // Search filter
    if (bulkSearchTerm) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(bulkSearchTerm.toLowerCase())
      );
    }

    // Category filter - fix to properly filter by category
    if (bulkCategoryFilter) {
      const categoryItems = allInventoryData.filter(
        (item) => item.category_id === bulkCategoryFilter
      );
      const categoryItemIds = categoryItems.map((item) => item.id);
      filtered = filtered.filter((item) => categoryItemIds.includes(item.id));
    }

    // Stock status filter
    if (bulkStockFilter !== "all") {
      filtered = filtered.filter((item) => {
        const originalItem = allInventoryData.find((i) => i.id === item.id);
        if (!originalItem) return false;

        if (bulkStockFilter === "empty") {
          return originalItem.current_stock === 0;
        } else if (bulkStockFilter === "low") {
          return (
            originalItem.current_stock > 0 &&
            originalItem.current_stock <= originalItem.minimum_stock
          );
        }
        return true;
      });
    }

    return filtered;
  }, [
    bulkUpdates,
    bulkSearchTerm,
    bulkCategoryFilter,
    bulkStockFilter,
    allInventoryData,
  ]);

  // Paginate filtered items
  const paginatedBulkItems = useMemo(() => {
    const startIndex = (bulkCurrentPage - 1) * bulkPageSize;
    const endIndex = startIndex + bulkPageSize;
    return filteredBulkItems.slice(startIndex, endIndex);
  }, [filteredBulkItems, bulkCurrentPage, bulkPageSize]);

  const totalBulkPages = Math.ceil(filteredBulkItems.length / bulkPageSize);

  const handleBulkSelectAll = (checked) => {
    setBulkUpdates((prev) =>
      prev.map((item) => {
        // Only update items that are in the current filtered results
        const isInFiltered = filteredBulkItems.some(
          (filteredItem) => filteredItem.id === item.id
        );
        if (isInFiltered) {
          return {
            ...item,
            quantity: checked ? Math.max(item.quantity, 1) : 0,
          };
        }
        return item;
      })
    );
  };

  const getCategoryName = (categoryId) => {
    const categoryData =
      allCategoriesData.length > 0 ? allCategoriesData : (Array.isArray(categories) ? categories : []);
    if (!categoryData.length) return "Uncategorized";
    const category = categoryData.find((cat) => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  const handleSingleUpdate = async () => {
    if (!selectedItem) return;

    if (singleUpdate.quantity <= 0) {
      Swal.fire("Error!", "Quantity must be greater than 0", "error");
      return;
    }

    // if (!singleUpdate.reason.trim()) {
    //   Swal.fire(
    //     "Error!",
    //     "Please provide a reason for the stock update",
    //     "error"
    //   );
    //   return;
    // }

    try {
      setLoading(true);

      // Use the correct endpoint and payload structure
      await patchRequest(`/inventory/${selectedItem.id}/stock`, {
        quantity: singleUpdate.quantity,
        type: singleUpdate.type === "add" ? "in" : "out",
        reason: singleUpdate.reason,
        warehouse_id: singleUpdate.warehouse_id,
      });

      Swal.fire("Success!", "Stock updated successfully", "success");
      onSuccess && onSuccess();
      onHide();
    } catch (error) {
      Swal.fire("Error!", "Failed to update stock", "error");
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkReason.trim()) {
      Swal.fire(
        "Error!",
        "Please provide a reason for the bulk update",
        "error"
      );
      return;
    }

    const selectedItems = bulkUpdates.filter((item) => item.quantity > 0);

    if (selectedItems.length === 0) {
      Swal.fire("Error!", "Please add quantity to at least one item", "error");
      return;
    }

    try {
      setLoading(true);

      // Prepare bulk update payload
      const updates = selectedItems.map((item) => ({
        inventory_id: item.id,
        quantity: item.quantity,
        type: bulkType,
        reason: bulkReason,
      })); // Use the new bulk update endpoint
      await postRequest("/inventory/bulk-update-stock", {
        updates: updates,
      });

      Swal.fire(
        "Success!",
        `${selectedItems.length} items updated successfully`,
        "success"
      );
      onSuccess && onSuccess();
      onHide();
    } catch (error) {
      Swal.fire("Error!", "Failed to update stocks", "error");
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkItemChange = (itemId, field, value) => {
    setBulkUpdates((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const getStockStatus = (current, minimum) => {
    if (current === 0) {
      return { status: "empty", color: "#dc2626", label: "Habis" };
    } else if (current <= minimum) {
      return { status: "low", color: "#d97706", label: "Stok Rendah" };
    } else {
      return { status: "good", color: "#059669", label: "Stok Baik" };
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconPackage size={20} style={{ marginRight: "8px" }} />
          Update Stock
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          <Tab
            eventKey="single"
            title="Single Item Update"
            disabled={!selectedItem}
          >
            {selectedItem && (
              <Card>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <div>
                        <h6>{selectedItem.name}</h6>
                        <p className="text-muted mb-2">
                          Current Stock: {selectedItem.current_stock}{" "}
                          {selectedItem.unit}
                        </p>
                        <Badge
                          style={{
                            backgroundColor: getStockStatus(
                              selectedItem.current_stock,
                              selectedItem.minimum_stock
                            ).color,
                          }}
                          text="white"
                        >
                          {
                            getStockStatus(
                              selectedItem.current_stock,
                              selectedItem.minimum_stock
                            ).label
                          }
                        </Badge>
                      </div>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Update Type</Form.Label>
                        <div className="d-flex gap-2">
                          <Button
                            variant={
                              singleUpdate.type === "add"
                                ? "success"
                                : "outline-success"
                            }
                            onClick={() =>
                              setSingleUpdate((prev) => ({
                                ...prev,
                                type: "add",
                              }))
                            }
                            className="flex-fill"
                            style={{
                              padding: "12px 16px",
                              fontSize: "16px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              minHeight: "48px",
                            }}
                          >
                            <IconPlus size={20} />
                            Stock In (+)
                          </Button>
                          <Button
                            variant={
                              singleUpdate.type === "subtract"
                                ? "danger"
                                : "outline-danger"
                            }
                            onClick={() =>
                              setSingleUpdate((prev) => ({
                                ...prev,
                                type: "subtract",
                              }))
                            }
                            className="flex-fill"
                            style={{
                              padding: "12px 16px",
                              fontSize: "16px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              minHeight: "48px",
                            }}
                          >
                            <IconMinus size={20} />
                            Stock Out (-)
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Warehouse *</Form.Label>
                        <Form.Select
                          value={singleUpdate.warehouse_id}
                          onChange={(e) =>
                            setSingleUpdate((prev) => ({
                              ...prev,
                              warehouse_id: e.target.value,
                            }))
                          }
                          disabled
                        >
                          <option value="">Select Warehouse</option>
                          {warehouses.map((warehouse) => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Current Stock in Selected Warehouse
                        </Form.Label>
                        <div
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          {(() => {
                            const selectedWarehouseStock =
                              selectedItem.warehouse_stocks?.find(
                                (ws) =>
                                  ws.warehouse_id === singleUpdate.warehouse_id
                              );
                            return selectedWarehouseStock
                              ? selectedWarehouseStock.stock
                              : 0;
                          })()}{" "}
                          {selectedItem.unit}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Quantity</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            {singleUpdate.type === "add" ? (
                              <IconPlus size={16} />
                            ) : (
                              <IconMinus size={16} />
                            )}
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            value={singleUpdate.quantity}
                            onChange={(e) =>
                              setSingleUpdate((prev) => ({
                                ...prev,
                                quantity: parseInt(e.target.value) || 0,
                              }))
                            }
                            placeholder="Enter quantity"
                          />
                          <InputGroup.Text>{selectedItem.unit}</InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>New Stock Total</Form.Label>
                        <div
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          {(() => {
                            const selectedWarehouseStock =
                              selectedItem.warehouse_stocks?.find(
                                (ws) =>
                                  ws.warehouse_id === singleUpdate.warehouse_id
                              );
                            const currentStock = selectedWarehouseStock
                              ? selectedWarehouseStock.stock
                              : 0;
                            return singleUpdate.type === "add"
                              ? currentStock + singleUpdate.quantity
                              : Math.max(
                                  0,
                                  currentStock - singleUpdate.quantity
                                );
                          })()}{" "}
                          {selectedItem.unit}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Reason *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={singleUpdate.reason}
                      onChange={(e) =>
                        setSingleUpdate((prev) => ({
                          ...prev,
                          reason: e.target.value,
                        }))
                      }
                      placeholder="Enter reason for stock update (e.g., Restock, Usage, Adjustment)"
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            )}
          </Tab>

          <Tab eventKey="bulk" title="Bulk Stock Update">
            <Alert variant="info">
              <IconShoppingCart size={16} style={{ marginRight: "8px" }} />
              Select items and quantities to update stock levels. Use filters to
              find items quickly.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>Reason for Bulk Update *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Enter reason for bulk stock update (e.g., Monthly restock, Usage adjustment, Inventory correction)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Update Type</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={bulkType === "in" ? "success" : "outline-success"}
                  onClick={() => setBulkType("in")}
                  className="flex-fill"
                  style={{
                    padding: "12px 16px",
                    fontSize: "16px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    minHeight: "48px",
                  }}
                >
                  <IconPlus size={20} />
                  Stock In (+)
                </Button>
                <Button
                  variant={bulkType === "out" ? "danger" : "outline-danger"}
                  onClick={() => setBulkType("out")}
                  className="flex-fill"
                  style={{
                    padding: "12px 16px",
                    fontSize: "16px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    minHeight: "48px",
                  }}
                >
                  <IconMinus size={20} />
                  Stock Out (-)
                </Button>
              </div>
            </Form.Group>

            {/* Search and Filters */}
            <Row className="mb-3 g-2">
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search items..."
                    value={bulkSearchTerm}
                    onChange={(e) => {
                      setBulkSearchTerm(e.target.value);
                      setBulkCurrentPage(1); // Reset to first page when searching
                    }}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={bulkCategoryFilter}
                  onChange={(e) => {
                    setBulkCategoryFilter(e.target.value);
                    setBulkCurrentPage(1); // Reset to first page when filtering
                  }}
                >
                  <option value="">All Categories</option>
                  {(allCategoriesData.length > 0
                    ? allCategoriesData
                    : (Array.isArray(categories) ? categories : [])
                  ).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={bulkStockFilter}
                  onChange={(e) => {
                    setBulkStockFilter(e.target.value);
                    setBulkCurrentPage(1); // Reset to first page when filtering
                  }}
                >
                  <option value="all">All Stock Levels</option>
                  <option value="low">Low Stock Items</option>
                  <option value="empty">Out of Stock</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <div className="d-flex gap-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleBulkSelectAll(true)}
                    title={`${
                      bulkType === "in" ? "Add" : "Subtract"
                    } quantity 1 to all filtered items`}
                  >
                    <IconSelectAll size={14} />
                    {bulkType === "in" ? "Add" : "Subtract"} All Filtered
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleBulkSelectAll(false)}
                    title="Clear all quantities"
                  >
                    <IconDeselect size={14} />
                    Clear All
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Results Summary */}
            <div className="mb-3 text-muted small">
              Showing {paginatedBulkItems.length} of {filteredBulkItems.length}{" "}
              items
              {bulkUpdates.filter((item) => item.quantity > 0).length > 0 && (
                <span className="ms-2">
                  • {bulkUpdates.filter((item) => item.quantity > 0).length}{" "}
                  items with quantity{" "}
                  {bulkType === "in" ? "added" : "subtracted"}
                </span>
              )}
            </div>

            <Card>
              <Card.Body style={{ padding: 0 }}>
                <Table striped hover style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Category</th>
                      <th style={{ textAlign: "center" }}>Current Stock</th>
                      <th style={{ textAlign: "center" }}>
                        {bulkType === "in" ? "Add" : "Subtract"} Quantity
                      </th>
                      <th style={{ textAlign: "center" }}>New Total</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBulkItems.map((item) => {
                      const originalItem = allInventoryData.find(
                        (i) => i.id === item.id
                      );
                      const stockStatus = getStockStatus(
                        item.currentStock,
                        originalItem?.minimum_stock || 0
                      );
                      const hasQuantity = item.quantity > 0;
                      return (
                        <tr
                          key={item.id}
                          style={{
                            backgroundColor: hasQuantity
                              ? "#f0f9ff"
                              : "transparent",
                          }}
                        >
                          <td>
                            <div>
                              <div style={{ fontWeight: "500" }}>
                                {item.name}
                              </div>
                              {originalItem?.qr_code && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                  }}
                                >
                                  QR: {originalItem.qr_code}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            {originalItem &&
                              getCategoryName(originalItem.category_id)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {item.currentStock} {item.unit}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Form.Control
                              type="number"
                              min="0"
                              size="sm"
                              style={{ width: "100px", textAlign: "center" }}
                              value={item.quantity}
                              onChange={(e) =>
                                handleBulkItemChange(
                                  item.id,
                                  "quantity",
                                  parseInt(e.target.value) || 0
                                )
                              }
                              placeholder="0"
                            />
                          </td>
                          <td
                            style={{ textAlign: "center", fontWeight: "500" }}
                          >
                            {bulkType === "in"
                              ? item.currentStock + item.quantity
                              : Math.max(
                                  0,
                                  item.currentStock - item.quantity
                                )}{" "}
                            {item.unit}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Badge
                              style={{
                                backgroundColor: stockStatus.color,
                              }}
                              text="white"
                            >
                              {stockStatus.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                    {paginatedBulkItems.length === 0 && (
                      <tr>
                        <td
                          colSpan="6"
                          style={{ textAlign: "center", padding: "40px" }}
                        >
                          <IconPackage size={48} color="#6b7280" />
                          <div style={{ marginTop: "16px", color: "#6b7280" }}>
                            No items match your filters
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>

              {/* Pagination Controls */}
              {totalBulkPages > 1 && (
                <Card.Footer>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">Show:</span>
                        <Form.Select
                          size="sm"
                          style={{ width: "80px" }}
                          value={bulkPageSize}
                          onChange={(e) => {
                            setBulkPageSize(parseInt(e.target.value));
                            setBulkCurrentPage(1);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </Form.Select>
                        <span className="text-muted small">per page</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <span className="text-muted small">
                          Page {bulkCurrentPage} of {totalBulkPages}
                        </span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={bulkCurrentPage === 1}
                          onClick={() =>
                            setBulkCurrentPage(bulkCurrentPage - 1)
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={bulkCurrentPage === totalBulkPages}
                          onClick={() =>
                            setBulkCurrentPage(bulkCurrentPage + 1)
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <IconX size={16} style={{ marginRight: "8px" }} />
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={
            activeTab === "single" ? handleSingleUpdate : handleBulkUpdate
          }
          disabled={loading}
        >
          {loading ? (
            "Updating..."
          ) : (
            <>
              <IconCheck size={16} style={{ marginRight: "8px" }} />
              Update Stock
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockUpdateModal;
